<main class="web-comparator">
	<header class="intro">
		<h1>{{ 'webComparator.title' | transloco }}</h1>
		<p>{{ 'webComparator.subtitle' | transloco }}</p>
	</header>

	@if (isLoadingConfig) {
		<p class="notice">{{ 'webComparator.loading' | transloco }}</p>
	} @else if (closed) {
		<p class="notice warning">{{ 'webComparator.errors.closed' | transloco }}</p>
	} @else {
		<!-- 1. Upload -->
		@if (step === 'upload') {
			<section class="panel">
				<app-file-dropzone
					[title]="'webComparator.upload.title' | transloco"
					[subtitle]="'webComparator.upload.subtitle' | transloco"
					[selectText]="'webComparator.upload.select' | transloco"
					[buttonLabel]="'webComparator.upload.button' | transloco"
					[uploadingText]="'webComparator.upload.reading' | transloco"
					[accept]="accept"
					[multiple]="false"
					iconName="FileText"
					size="compact"
					[state]="isReading ? 'uploading' : 'idle'"
					(filesSelected)="onFilesSelected($event)"
				></app-file-dropzone>
				<p class="hint">{{ 'webComparator.upload.privacy' | transloco }}</p>
				@if (unreadable) {
					<p class="notice warning">{{ 'webComparator.unreadable' | transloco }}</p>
				}
			</section>
		}

		<!-- 2. Result: one card per marketer -->
		@if (step === 'result' && result) {
			<section class="panel">
				<dl class="bill-summary">
					@for (item of billSummary; track item.label) {
						<div>
							<dt>{{ item.label }}</dt>
							<dd>{{ item.value }}</dd>
						</div>
					}
				</dl>
				<p class="current">
					{{ 'webComparator.result.current' | transloco: { amount: euros(result.currentTotal) } }}
				</p>

				@if (savingCards.length > 0) {
					<h2>{{ 'webComparator.result.title' | transloco }}</h2>
					<ul class="cards" role="list">
						@for (card of savingCards; track card.offerId; let first = $first) {
							<li class="card" [class.best]="first">
								@if (first) {
									<span class="badge">{{ 'webComparator.card.best' | transloco }}</span>
								}
								<p class="marketer">{{ card.marketerName }}</p>
								<p class="offer">{{ card.offerName }}</p>
								<p class="savings">
									<span class="label">{{ 'webComparator.card.savings' | transloco }}</span>
									<span class="amount">{{ euros(card.savings) }}</span>
									<span class="period">{{ 'webComparator.card.perMonth' | transloco }}</span>
								</p>
								@if (card.savingsPct !== null) {
									<p class="pct">{{ 'webComparator.card.pct' | transloco: { pct: card.savingsPct } }}</p>
								}
								<p class="price">{{ 'webComparator.card.price' | transloco: { amount: euros(card.monthTotal) } }}</p>
								@if (contractingEnabled) {
									<app-button
										[label]="'webComparator.card.contract' | transloco"
										size="medium"
										(onClick)="contract(card)"
									></app-button>
								}
							</li>
						}
					</ul>
				} @else {
					<p class="notice">{{ 'webComparator.result.noSavings' | transloco }}</p>
				}

				<p class="hint">{{ 'webComparator.result.disclaimer' | transloco }}</p>
				<div class="actions">
					<app-button
						[label]="'webComparator.result.another' | transloco"
						variant="secondary"
						size="medium"
						(onClick)="restart()"
					></app-button>
				</div>
			</section>
		}

		<!-- 3. Contract -->
		@if (step === 'contract' && selected) {
			<section class="panel">
				<h2>
					{{ 'webComparator.form.title' | transloco: { marketer: selected.marketerName, offer: selected.offerName } }}
				</h2>
				<p class="hint">{{ 'webComparator.form.savingsReminder' | transloco: { amount: euros(selected.savings) } }}</p>

				<form [formGroup]="form" (ngSubmit)="send()" novalidate>
					<fieldset class="kinds">
						<legend>{{ 'webComparator.form.kind' | transloco }}</legend>
						@for (kind of kinds; track kind) {
							<label class="kind">
								<input type="radio" formControlName="kind" [value]="kind" />
								{{ 'webComparator.form.kinds.' + kind | transloco }}
							</label>
						}
					</fieldset>

					<label class="field">
						<span>{{ (isCompany ? 'webComparator.form.companyName' : 'webComparator.form.name') | transloco }}</span>
						<input type="text" formControlName="name" autocomplete="name" [class.invalid]="invalid('name')" />
					</label>
					<label class="field">
						<span>{{ (isCompany ? 'webComparator.form.cif' : 'webComparator.form.taxId') | transloco }}</span>
						<input type="text" formControlName="taxId" [class.invalid]="invalid('taxId')" />
					</label>
					@if (isCompany) {
						<label class="field">
							<span>{{ 'webComparator.form.representativeName' | transloco }}</span>
							<input type="text" formControlName="representativeName" />
						</label>
						<label class="field">
							<span>{{ 'webComparator.form.representativeTaxId' | transloco }}</span>
							<input type="text" formControlName="representativeTaxId" />
						</label>
					}
					<label class="field">
						<span>{{ 'webComparator.form.email' | transloco }}</span>
						<input type="email" formControlName="email" autocomplete="email" [class.invalid]="invalid('email')" />
					</label>
					<label class="field">
						<span>{{ 'webComparator.form.phone' | transloco }}</span>
						<input type="tel" formControlName="phone" autocomplete="tel" [class.invalid]="invalid('phone')" />
					</label>
					<label class="field">
						<span>{{ 'webComparator.form.iban' | transloco }}</span>
						<input type="text" formControlName="iban" placeholder="ES00 0000 0000 0000 0000 0000" [class.invalid]="invalid('iban')" />
					</label>
					<label class="field">
						<span>{{ 'webComparator.form.cups' | transloco }}</span>
						<input type="text" formControlName="cupsCode" [class.invalid]="invalid('cupsCode')" />
						<small>{{ 'webComparator.form.cupsHint' | transloco }}</small>
					</label>
					<label class="field">
						<span>{{ 'webComparator.form.supplyAddress' | transloco }}</span>
						<input type="text" formControlName="supplyAddress" autocomplete="street-address" />
					</label>

					<div class="field">
						<span>{{ (isCompany ? 'webComparator.form.dniCompany' : 'webComparator.form.dni') | transloco }}</span>
						<label class="file-picker">
							<input type="file" [accept]="documentAccept" multiple (change)="onDocumentsSelected($event)" />
							{{ 'webComparator.form.addDocument' | transloco }}
						</label>
						@for (file of documents; track $index) {
							<p class="file">
								{{ file.name }}
								<button type="button" class="link-button" (click)="removeDocument($index)">
									{{ 'webComparator.form.removeDocument' | transloco }}
								</button>
							</p>
						}
					</div>

					<label class="check">
						<input type="checkbox" formControlName="acceptPrivacy" />
						<span>{{ 'webComparator.form.privacy' | transloco }}</span>
					</label>

					<div class="actions">
						<app-button
							[label]="'webComparator.form.back' | transloco"
							variant="secondary"
							size="medium"
							(onClick)="backToResult()"
						></app-button>
						<app-button
							[label]="'webComparator.form.submit' | transloco"
							type="submit"
							size="medium"
							[loading]="isSending"
						></app-button>
					</div>
				</form>
			</section>
		}

		<!-- 4. Done -->
		@if (step === 'done') {
			<section class="panel done">
				<h2>{{ 'webComparator.done.title' | transloco }}</h2>
				<p>{{ 'webComparator.done.text' | transloco }}</p>
				<app-button
					[label]="'webComparator.result.another' | transloco"
					variant="secondary"
					size="medium"
					(onClick)="restart()"
				></app-button>
			</section>
		}

		@if (error) {
			<p class="notice warning" role="alert">{{ error }}</p>
		}
	}

	<!-- Cloudflare Turnstile: invisible unless Cloudflare asks the visitor to click. -->
	<div #turnstileBox class="turnstile"></div>
</main>
